import { useMemo } from 'react';
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClusterUserCreateDto, ClusterUserDto, ClusterUserUpdateDto, ClusterUsersService } from 'src/api/v2';
import { CLUSTERS_KEY, CLUSTER_USERS_KEY, USER_KEY } from 'src/constants/hook-keys';
import { useAuth } from 'src/features/authorization';
import { useCluster } from 'src/features/cluster';
import useEnvironment from 'src/hooks/use-environment';

// The generated service is stateless, so one shared instance avoids an allocation per hook call.
const service = new ClusterUsersService();

// Stable identity so consumers don't see a new array on every render before the query resolves.
const EMPTY_USERS: ClusterUserDto[] = [];

export type UseUsersOptions = Omit<
  UseQueryOptions<ClusterUserDto[], any, ClusterUserDto[], any>,
  'queryKey' | 'queryFn'
>;

/**
 * Read-only access to the cluster users. Prefer this over `useUsers` in list items and other
 * render-heavy components: it registers a single query observer instead of a query plus five
 * mutation observers.
 */
export function useUsersQuery(options?: UseUsersOptions) {
  const { cluster } = useCluster();
  const { checkAccess } = useAuth();

  const { read } = checkAccess('AccessControl');

  const { data, ...rest } = useQuery({
    queryKey: [CLUSTER_USERS_KEY],
    queryFn: () => service.getClusterUsers(),
    ...options,
    enabled: !!cluster && read && options?.enabled !== false,
  });

  return {
    users: data ?? EMPTY_USERS,
    ...rest,
  };
}

export type AdvisorLookup = {
  advisorsById: Map<string, ClusterUserDto>;
  advisorNameById: Map<string, string>;
  advisorInitialsById: Map<string, string>;
  advisorColorClassById: Map<string, string>;
};

// Keyed on the cached users array so the maps and the createdOn sort are built once and shared,
// rather than once per component instance.
const advisorLookupCache = new WeakMap<object, AdvisorLookup>();

const getAdvisorLookup = (users: ClusterUserDto[]): AdvisorLookup => {
  const cached = advisorLookupCache.get(users);
  if (cached) return cached;

  const byCreatedOn = [...users].sort(
    (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
  );

  const advisorInitialsById = new Map<string, string>();
  for (const user of users) {
    const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();
    if (initials) advisorInitialsById.set(user.id, initials);
  }

  const lookup: AdvisorLookup = {
    advisorsById: new Map(users.map((user) => [user.id, user])),
    advisorNameById: new Map(users.map((user) => [user.id, `${user.firstName} ${user.lastName}`])),
    advisorInitialsById,
    advisorColorClassById: new Map(
      byCreatedOn.map((user, index) => [user.id, `background-color--${(index % 7) + 1}`]),
    ),
  };

  advisorLookupCache.set(users, lookup);
  return lookup;
};

/** Read-only users plus O(1) advisor lookups. Safe to call from render-heavy components. */
export function useAdvisorLookup(options?: UseUsersOptions) {
  const { users, ...rest } = useUsersQuery(options);

  return { users, ...rest, ...getAdvisorLookup(users) };
}

export function useUsers(options?: UseUsersOptions) {
  const { users, ...rest } = useUsersQuery(options);

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (record: ClusterUserCreateDto) => service.create(record),
    onSuccess() {
      queryClient.invalidateQueries([CLUSTER_USERS_KEY]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ clusterUserId, record }: { clusterUserId: string; record: ClusterUserUpdateDto }) =>
      service.update(clusterUserId, record),
    onSuccess() {
      queryClient.invalidateQueries([CLUSTER_USERS_KEY]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (clusterUserId: string) => service.delete(clusterUserId),
    onSuccess() {
      queryClient.invalidateQueries([CLUSTER_USERS_KEY]);
    },
  });

  const { isDev, isTest } = useEnvironment();

  const activationBaseUrl = useMemo(() => {
    if (isDev) {
      return 'https://dev.flerbedriftsportal.no/user-activation/';
    }

    if (isTest) {
      return 'https://test.flerbedriftsportal.no/user-activation/';
    }

    return 'https://flerbedriftsportal.no/user-activation/';
  }, [isDev, isTest]);

  const inviteMutation = useMutation({
    mutationFn: (clusterUserId: string) =>
      service.invite({
        activationBaseUrl,
        clusterUserIds: [clusterUserId],
      }),
    onSuccess() {
      queryClient.invalidateQueries([CLUSTER_USERS_KEY]);
    },
  });

  const acceptMutation = useMutation({
    mutationFn: (invitationCode: string) => service.accept(invitationCode),
    onSuccess() {
      queryClient.invalidateQueries([CLUSTER_USERS_KEY]);
      queryClient.invalidateQueries([CLUSTERS_KEY]);
      queryClient.invalidateQueries([USER_KEY]);
    },
  });

  return {
    users,

    ...rest,

    mutations: {
      create: createMutation,
      update: updateMutation,
      delete: deleteMutation,
      invite: inviteMutation,
      accept: acceptMutation,
    },
  };
}
